import { useContext, useMemo, useRef, useState } from 'react';
import {
    AdditionTermsPageEventQuery,
    CreateHoldDocument,
    CreateHoldInput,
} from '@/graphql/graphql';
import parse from 'html-react-parser';
import { Box, Button, FormControl, FormLabel, Input } from '@mui/joy';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Checkbox, Typography } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import 'dayjs/locale/fr';
import { NavLink, useHistory } from 'react-router-dom';
import { darken, lighten } from 'polished';
import { createFingerPrint } from '@/utils/createFingerPrint';
import { getIpAddress } from '@/utils/getIpAddress';
import { CartContext } from '../providers/cart';
import {
    formatFlexiblePricingForHold,
    formatFlexiblePricingForTickets,
} from '@/utils/flexiblePricing/flexiblePricing-helper';
import { useApolloClient } from '@apollo/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

dayjs.extend(advancedFormat);

interface AdditionalTermsLoadedProps {
    event: AdditionTermsPageEventQuery['event'];
    setErrorAlert: (err: string) => void;
    organizerPrimaryColor: string;
}

export default function AdditionalTermsLoaded({
    event,
    setErrorAlert,
    organizerPrimaryColor,
}: AdditionalTermsLoadedProps) {
    const { t, i18n } = useTranslation();
    const history = useHistory();

    const contentRef = useRef(null);

    // Get base language code from i18n (e.g., 'en' from 'en_CA' or 'fr' from 'fr-CA')
    const baseLanguage = useMemo(() => {
        const lang = i18n.resolvedLanguage || i18n.language || 'en';
        // Extract base language (first 2 chars before _ or -)
        return lang.split(/[_-]/)[0]?.toLowerCase() || 'en';
    }, [i18n.resolvedLanguage, i18n.language]);

    // Format date with proper locale and capitalization
    const formattedDate = useMemo(() => {
        const dateStr = dayjs().locale(baseLanguage).format('MMMM Do, YYYY');
        // Capitalize first letter
        return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
    }, [baseLanguage]);

    const { cart, setCart, inputtedUnlockCodes, getCalculations } = useContext(CartContext);

    const [signature, setSignature] = useState<string>('');
    const [signatureCheck, setSignatureCheck] = useState<boolean>(false);
    const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
    const [disableBtn, setDisableBtn] = useState<boolean>(false);

    const apollo = useApolloClient();

    const { total } = useMemo(() => getCalculations(event), [getCalculations, event]);

    const disableButton = signature.length < 2 || !signatureCheck || !agreeToTerms || disableBtn;

    const onSubmit = async () => {
        setDisableBtn(true);
        try {
            const checkoutNotes = cart?.checkoutNotes;

            const ipAddress = await getIpAddress();

            const fingerprint = await createFingerPrint();

            const payload: CreateHoldInput = {
                fingerprint,
                eventId: event?.id,
                unlockCodes: inputtedUnlockCodes,
                flexibleAmounts: formatFlexiblePricingForHold(cart?.flexiblePricing),
                tickets: {
                    ...cart.quantities,
                    ...formatFlexiblePricingForTickets(cart?.flexiblePricing),
                },
                charged: {
                    amount: total,
                    currency: event!.currency,
                },
                promoCodes: cart.promoCodes,
                addons: cart.addons,
                tosAcceptanceDate: new Date().toISOString(),
                additionalTermsData: {
                    eSignatureAdoptionTimestamp: new Date().toISOString(),
                    termsAcceptanceTimestamp: new Date().toISOString(),
                    signature,
                    browserFingerprint: fingerprint,
                    ipAddress,
                },
            };

            if (event?.checkoutCustomFields?.[0]?.id && checkoutNotes?.trim()) {
                payload.customValues = {
                    [event.checkoutCustomFields[0].id]: checkoutNotes.trim(),
                };
            }

            const { data } = await apollo.mutate({
                mutation: CreateHoldDocument,
                variables: {
                    payload,
                },
            });

            if (!data?.createHold) {
                throw Error('Hold creation failed');
            }

            setCart({
                hold: {
                    id: data.createHold.id,
                    expiresAt: data.createHold.expiresAt,
                    visitorId: fingerprint,
                },
            });

            setDisableBtn(false);
            history.push('/payment');
        } catch (err) {
            console.error(err);
            setErrorAlert(t('additionalTerms.errors.holdCreationFailed', 'Error: There has been an issue creating your hold.'));
            setDisableBtn(false);
        }
    };

    const generatePDF = async () => {
        if (!contentRef.current) return;

        try {
            const canvas = await html2canvas(contentRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
            });

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const imgWidth = 210;
            const pageHeight = 295;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const padding = 10;
            const imgData = canvas.toDataURL('image/png');
            pdf.addImage(
                imgData,
                'PNG',
                padding,
                padding,
                imgWidth - padding * 2,
                imgHeight - padding * 2
            );

            if (imgHeight > pageHeight) {
                let remainingHeight = imgHeight;
                let position = 0;

                while (remainingHeight > 0) {
                    position -= pageHeight;
                    remainingHeight -= pageHeight;

                    if (remainingHeight > 0) {
                        pdf.addPage();
                        pdf.addImage(
                            imgData,
                            'PNG',
                            padding,
                            position,
                            imgWidth - padding * 2,
                            imgHeight
                        );
                    }
                }
            }

            pdf.save('terms.pdf');
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    return (
        <Box sx={{ mt: 4, pb: 6 }}>
            <Box
                ref={contentRef}
                sx={{ width: '100%', height: '549px', overflowY: 'scroll', mb: 4 }}
                id="pdf-content"
            >
                {parse(event?.additionalTerms as string)}
            </Box>
            <FormControl required>
                <FormLabel sx={{ fontWeight: 600 }}>
                    {t('additionalTerms.signature.label', 'Please Sign by Entering Your Full Name')}
                </FormLabel>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Input
                        placeholder={t('additionalTerms.signature.placeholder', 'Please Sign by Entering Your Full Name')}
                        onChange={(e) => setSignature(e.target.value)}
                        sx={{
                            width: '100%',
                            mr: 2,
                            '--Input-focusedHighlight': 'none !important',
                            '&.Joy-focused': {
                                border: 'none',
                            },
                            '& input': {
                                '&:focus': {
                                    outline: 'none',
                                },
                            },
                        }}
                        size="lg"
                    />
                    <Button
                        size="md"
                        sx={{
                            width: { sm: '225px', xs: '100%' },
                            mt: { sm: 0, xs: 2 },
                            backgroundColor: organizerPrimaryColor,
                            '&:hover': {
                                backgroundColor: darken(0.1, organizerPrimaryColor as string),
                            },
                        }}
                        startDecorator={<SaveAltIcon />}
                        onClick={generatePDF}
                    >
                        {t('additionalTerms.download.button', 'Download Terms')}
                    </Button>
                </Box>
            </FormControl>
            <Typography sx={{ mt: 2, mb: 3 }}>
                {t('additionalTerms.signedOn.label', 'Signed on')} <strong>{formattedDate}</strong>
            </Typography>
            <Box>
                <FormControl sx={{ mb: 1 }} required>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            id="signature-check"
                            checked={signatureCheck}
                            onChange={(event) => {
                                setSignatureCheck(event.target.checked);
                            }}
                            sx={{
                                '& svg': {
                                    fill: organizerPrimaryColor,
                                },
                                mr: 0.5,
                            }}
                        />
                        <FormLabel sx={{ m: 0, alignSelf: 'center', fontSize: 16 }}>
                            {t('additionalTerms.signatureCheck.label', 'By entering my name above, I agree to use it here as an electronic signature and I understand it\nis as legally binding as a traditional signature.').split('\n').map((line, index, array) => (
                                <span key={index}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                </span>
                            ))}
                        </FormLabel>
                    </Box>
                </FormControl>
                <FormControl sx={{ mb: 2 }} required>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                        <Checkbox
                            id="agree-to-terms"
                            checked={agreeToTerms}
                            onChange={(event) => {
                                setAgreeToTerms(event.target.checked);
                            }}
                            sx={{
                                '& svg': {
                                    fill: organizerPrimaryColor,
                                },
                                mr: 0.5,
                            }}
                        />
                        <FormLabel sx={{ m: 0, alignSelf: 'center', fontSize: 16 }}>
                            {t('additionalTerms.agreeToTerms.label', 'I have read, understood and agree to the terms and conditions of the\nindicated above.').split('\n').map((line, index, array) => (
                                <span key={index}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                </span>
                            ))}
                        </FormLabel>
                    </Box>
                </FormControl>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    size="lg"
                    variant="outlined"
                    component={NavLink}
                    to={`/${event?.shortEventId}`}
                    sx={{
                        mr: 1,
                        border: `1px solid ${organizerPrimaryColor}`,
                        color: organizerPrimaryColor,
                        '&:hover': {
                            backgroundColor: lighten(0.5, organizerPrimaryColor as string),
                        },
                    }}
                >
                    {t('additionalTerms.decline.button', 'Decline')}
                </Button>
                <Button
                    disabled={disableButton}
                    sx={{
                        backgroundColor: organizerPrimaryColor,
                        '&:hover': {
                            backgroundColor: darken(0.1, organizerPrimaryColor as string),
                        },
                    }}
                    onClick={onSubmit}
                >
                    {t('additionalTerms.proceed.button', 'Agree & Proceed')}
                </Button>
            </Box>
        </Box>
    );
}
